import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { UserMaintenanceRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { MaintenanceComponentTabReferrer } from '../enums/maintenance-component-tab-referrer.enum';
import { IProject, IProjectExportProcess } from '../models/project.model';
import { ProjectApiService } from '../services/project-api.service';
import {
  AssignMaintenanceJobsToProject,
  ClearCurrentProject,
  CompleteProject,
  CreateProject,
  DeleteProject,
  ExportProjectToMarineManager,
  LoadProjectById,
  LoadProjectExportErrors,
  LoadProjects,
  RemoveMaintenanceJobsFromProject,
  SyncLinkWithMarineManager,
  SyncProjectWithMarineManager,
  UpdateProject,
  UpdateProjectStatus,
} from './project.actions';

export interface ProjectStateModel {
  projects: IProject[];
  selectedProject: IProject | null;
  exportErrors: IProjectExportProcess[];
  includeCompleted: boolean;
  totalPages: number;
}

const PROJECT_STATE_TOKEN = new StateToken<ProjectStateModel>('project');

@State<ProjectStateModel>({
  name: PROJECT_STATE_TOKEN,
  defaults: {
    projects: [],
    selectedProject: null,
    exportErrors: [],
    includeCompleted: false,
    totalPages: 0,
  },
})
@Injectable({
  providedIn: 'root',
})
export class ProjectState {
  constructor(private projectApiService: ProjectApiService) {}

  @Action(LoadProjects, { cancelUncompleted: true })
  loadProjects(ctx: StateContext<ProjectStateModel>, { filter }: LoadProjects) {
    return this.projectApiService.getProjects(filter).pipe(
      tap((response) => {
        ctx.patchState({
          projects: response.data,
          totalPages: response.totalPages,
          includeCompleted:
            filter?.additional?.includeCompleted !== undefined
              ? filter.additional.includeCompleted
              : ctx.getState().includeCompleted,
        });
      })
    );
  }

  @Action(LoadProjectById)
  loadProjectById(ctx: StateContext<ProjectStateModel>, action: LoadProjectById) {
    return this.projectApiService.getProjectById(action.id).pipe(
      tap((project) => {
        ctx.patchState({
          selectedProject: project,
        });
      })
    );
  }

  @Action(CreateProject)
  @OnSuccess({ message: 'projectAdded' })
  createProject(ctx: StateContext<ProjectStateModel>, action: CreateProject) {
    return this.projectApiService.createProject(action.project).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(UserMaintenanceRoutes.Main, {
          queryParams: { activeTab: MaintenanceComponentTabReferrer.Projects },
        });
      })
    );
  }

  @Action(UpdateProject)
  @OnSuccess({ message: 'projectUpdated' })
  updateProject(ctx: StateContext<ProjectStateModel>, { project }: UpdateProject) {
    return this.projectApiService.updateProject(project).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(UserMaintenanceRoutes.Main, {
          queryParams: { activeTab: MaintenanceComponentTabReferrer.Projects },
        });
      })
    );
  }

  @Action(DeleteProject)
  @OnSuccess({ message: 'projectDeleted' })
  deleteProject(ctx: StateContext<ProjectStateModel>, { id }: DeleteProject) {
    return this.projectApiService.deleteProject(id).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(UserMaintenanceRoutes.Main, {
          queryParams: { activeTab: MaintenanceComponentTabReferrer.Projects },
        });
      })
    );
  }

  @Action(UpdateProjectStatus)
  updateProjectStatus(
    ctx: StateContext<ProjectStateModel>,
    { projectId, status }: UpdateProjectStatus
  ) {
    return this.projectApiService.updateProjectStatus(projectId, status);
  }

  @Action(AssignMaintenanceJobsToProject)
  @OnSuccess({ message: 'jobsAssignedToProjectSuccessfully' })
  assignMaintenanceJobsToProject(
    ctx: StateContext<ProjectStateModel>,
    { projectId, maintenanceJobIds }: AssignMaintenanceJobsToProject
  ) {
    return this.projectApiService.assignMaintenanceJobsToProject(projectId, maintenanceJobIds);
  }

  @Action(RemoveMaintenanceJobsFromProject)
  @OnSuccess({ message: 'jobsRemovedFromProjectSuccessfully' })
  removeMaintenanceJobsFromProject(
    ctx: StateContext<ProjectStateModel>,
    { projectId, maintenanceJobIds }: RemoveMaintenanceJobsFromProject
  ) {
    return this.projectApiService.removeMaintenanceJobsFromProject(projectId, maintenanceJobIds);
  }

  @Action(ClearCurrentProject)
  clearCurrentProject(ctx: StateContext<ProjectStateModel>) {
    ctx.patchState({
      selectedProject: null,
      exportErrors: [],
    });
  }

  @Action(LoadProjectExportErrors)
  loadProjectExportErrors(
    ctx: StateContext<ProjectStateModel>,
    { projectId }: LoadProjectExportErrors
  ) {
    return this.projectApiService.getExportErrors(projectId).pipe(
      tap((exportErrors) => {
        ctx.patchState({ exportErrors });
      })
    );
  }

  @Action(ExportProjectToMarineManager)
  @OnSuccess({ message: 'projectExportedToMarineManager' })
  exportProjectToMarineManager(
    ctx: StateContext<ProjectStateModel>,
    { projectId }: ExportProjectToMarineManager
  ) {
    return this.projectApiService.exportToMarineManager(projectId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(UserMaintenanceRoutes.Main, {
          queryParams: { activeTab: MaintenanceComponentTabReferrer.Projects },
        });
      })
    );
  }

  @Action(SyncProjectWithMarineManager)
  @OnSuccess({ message: 'projectSyncedWithMarineManager' })
  syncProjectWithMarineManager(
    ctx: StateContext<ProjectStateModel>,
    { projectId }: SyncProjectWithMarineManager
  ) {
    return this.projectApiService.syncWithMarineManager(projectId);
  }

  @Action(SyncLinkWithMarineManager)
  @OnSuccess({ message: 'performSyncedWithMarineManager' })
  syncLinkWithMarineManager(
    ctx: StateContext<ProjectStateModel>,
    { projectId, linkId }: SyncLinkWithMarineManager
  ) {
    return this.projectApiService.syncLinkWithMarineManager(projectId, linkId);
  }

  @Action(CompleteProject)
  @OnSuccess({ message: 'projectCompleted' })
  completeProject(ctx: StateContext<ProjectStateModel>, { projectId }: CompleteProject) {
    return this.projectApiService.completeProject(projectId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(UserMaintenanceRoutes.Main, {
          queryParams: { activeTab: MaintenanceComponentTabReferrer.Projects },
        });
      })
    );
  }

  @Selector()
  static getProjects(state: ProjectStateModel): IProject[] {
    return state.projects;
  }

  @Selector()
  static getSelectedProject(state: ProjectStateModel): IProject | null {
    return state.selectedProject;
  }

  @Selector()
  static getIncludeCompleted(state: ProjectStateModel): boolean {
    return state.includeCompleted;
  }

  @Selector()
  static getTotalPages(state: ProjectStateModel): number {
    return state.totalPages;
  }

  @Selector()
  static getExportErrors(state: ProjectStateModel): IProjectExportProcess[] {
    return state.exportErrors;
  }
}
